import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, imgUrl } from "@/lib/api";
import { BRAND, CATEGORIES, CONTACT, NAV_LINKS } from "@/data/siteData";

const SiteContext = createContext(null);

/** Build a wa.me link from the (CMS-editable) contact settings. */
export function waLinkFor(contact, message) {
  const number = (contact?.whatsapp || CONTACT.whatsapp || "").replace(/\D/g, "");
  const msg = message || contact?.whatsapp_message || "Hi, I'm interested in your products.";
  return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
}

export function SiteProvider({ children }) {
  const [site, setSite] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/site")
      .then((r) => {
        if (alive) setSite(r.data);
      })
      .catch(() => {
        /* static fallbacks keep the site identical when the CMS API is unavailable */
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const fav = site?.settings?.brand?.favicon;
    if (fav) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = imgUrl(fav);
    }
  }, [site]);

  const value = useMemo(() => {
    const sections = Array.isArray(site?.sections) ? site.sections : null;
    return {
      loaded: site !== null,
      brand: { cta_label: "Chat on WhatsApp", ...BRAND, ...(site?.settings?.brand || {}) },
      contact: { ...CONTACT, ...(site?.settings?.contact || {}) },
      razorpay: site?.settings?.razorpay || { enabled: false, currency: "INR" },
      sections,
      section: (type) => sections?.find((s) => s.type === type)?.content || null,
      nav: site?.nav || NAV_LINKS.map((l) => ({ label: l.label, url: l.hash })),
      quickLinks: site?.quickLinks || NAV_LINKS.map((l) => ({ label: l.label, url: l.hash })),
      categories:
        site?.categories ||
        CATEGORIES.map((c) => ({ slug: c.id, name: c.name, tagline: c.tagline, image: c.image })),
    };
  }, [site]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);
